import { useEffect } from "react";
import { Field, useForm } from "react-final-form";
import type {
	Answer,
	AnswerChoice,
	AnyQuestion,
	CountryAndTinAnswer,
	CountryAndTinValue,
	PepRelationAnswer,
	PepRelationValue,
} from "../../../types";
import type { QuestionnaireFormValues } from "../../../types/form";
import { deepEqual, isObject } from "../../../utils";
import { formatFieldName, getAllSubQuestions } from "../utils";

type Props = {
	when: string;
	is: AnswerChoice<string> | AnswerChoice<CountryAndTinAnswer> | AnswerChoice<PepRelationAnswer>;
	children: React.ReactNode;
	subQuestion: AnyQuestion;
};

const areValuesEqual = (
	value1: string | CountryAndTinValue | PepRelationValue,
	value2: string | CountryAndTinAnswer | PepRelationAnswer,
) => {
	if (isObject(value1) && isObject(value2)) {
		return deepEqual(value1, value2.answerValue);
	}
	return value1 === value2;
};

export const QuestionnaireCondition = ({ when, is, children, subQuestion }: Props) => {
	const form = useForm<QuestionnaireFormValues, Partial<QuestionnaireFormValues>>();
	return (
		<Field<Array<Answer>>
			name={when}
			subscription={{ value: true }}
			format={(v) => v || []} // defaults the field to empty array instead of empty string
		>
			{({ input }) => {
				const isSubQuestionActive = input.value.some((v) =>
					areValuesEqual(v.answerValue, is.value),
				);
				// biome-ignore lint/correctness/useHookAtTopLevel: Not conditional
				useEffect(() => {
					if (!isSubQuestionActive) {
						const answersIdsToRemove = getAllSubQuestions([subQuestion]).map((s) => s.questionId);

						answersIdsToRemove.forEach((id) => {
							form.change(formatFieldName(id), undefined);
						});
					}
				}, [JSON.stringify(input.value)]);

				if (isSubQuestionActive) return children;

				return null;
			}}
		</Field>
	);
};
